import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faChevronDown } from '@fortawesome/free-solid-svg-icons';

const SenderReceiverCompanyAutocomplete = ({
  value,
  onChange,
  options,
  label,
  onAddNew,
  placeholder,
  error,
  errorMessage,
  companyType = 'both', // 'sender', 'receiver', or 'both'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term and company type
  useEffect(() => {
    let filtered = options.filter((option) => {
      const matchesSearch = option.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      
      // Filter by company type
      const matchesType = 
        companyType === 'both' || 
        option.company_type === companyType || 
        option.company_type === 'both';
      
      return matchesSearch && matchesType;
    });

    setFilteredOptions(filtered);
  }, [searchTerm, options, companyType]);

  // Set search term when value changes externally
  useEffect(() => {
    if (value) {
      const selectedOption = options.find((option) => option.name === value);
      if (selectedOption) {
        setSearchTerm(selectedOption.name);
      } else {
        setSearchTerm(value);
      }
    } else {
      setSearchTerm('');
    }
  }, [value, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    onChange(newValue);
  };

  const handleOptionSelect = (option) => {
    setSearchTerm(option.name);
    onChange(option.name);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleAddNew = () => {
    setIsOpen(false);
    if (onAddNew) {
      onAddNew();
    }
  };

  const getCompanyTypeLabel = (type) => {
    switch (type) {
      case 'sender':
        return 'Gönderici';
      case 'receiver':
        return 'Alıcı';
      case 'both':
        return 'Her İkisi';
      default:
        return type;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
        >
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`w-4 h-4 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
      </div>

      {error && errorMessage && (
        <p className="text-red-500 text-xs mt-1">{errorMessage}</p>
      )}

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {onAddNew && (
            <button
              type="button"
              onClick={handleAddNew}
              className="w-full px-4 py-2 text-left text-blue-600 hover:bg-blue-50 flex items-center border-b border-gray-200"
            >
              <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
              Yeni Firma Ekle
            </button>
          )}

          {filteredOptions.length === 0 ? (
            <div className="px-4 py-2 text-gray-500 text-sm">
              {searchTerm ? 'Sonuç bulunamadı' : 'Firma bulunamadı'}
            </div>
          ) : (
            filteredOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => handleOptionSelect(option)}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium text-gray-900">
                      {option.name}
                    </div>
                    {option.contact_person && (
                      <div className="text-sm text-gray-500">
                        {option.contact_person}
                      </div>
                    )}
                    {option.city && option.country && (
                      <div className="text-xs text-gray-400">
                        {option.city}, {option.country}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                    {getCompanyTypeLabel(option.company_type)}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SenderReceiverCompanyAutocomplete;
